// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import buildUnitarySystems from "@plumberssupply/App/Handlers/SystemBuilder/buildUnitarySystems";
import getUnitaryBrands from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryBrands";
import getUnitaryCoilWidths from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryCoilWidths";
import getUnitaryFurnaceBtus from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryFurnaceBtus";
import getUnitaryIndoorTypes from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryIndoorTypes";
import getUnitaryKilowattRatings from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryKilowattRatings";
import getUnitaryTons from "@plumberssupply/App/Handlers/SystemBuilder/getUnitaryTons";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import Button from "@plumberssupply/MobiusOverrides/Button/Button";
import LoadingOverlay from "@plumberssupply/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { SystemBuilderKey } from "@plumberssupply/XDL/constants";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SystemBuilderUnitarySpecificationViewStyles;
}

export interface SystemBuilderUnitarySpecificationViewStyles {
    title?: TypographyPresentationProps;
    select?: SelectPresentationProps;
    wrapper?: InjectableCss;
    divider?: InjectableCss;
    buttonWrapper?: InjectableCss;
    container?: InjectableCss;
    checkBox?: CheckboxPresentationProps;
}

const mapStateToProps = (state: CustomAppState) => ({
    step: state.xSystemBuilder.step,
    unitaryConfigurations: state.xSystemBuilder.unitaryConfigurations,
});

const mapDispatchToProps = {
    getUnitaryBrands,
    getUnitaryTons,
    getUnitaryFurnaceBtus,
    getUnitaryIndoorTypes,
    buildUnitarySystems,
    getUnitaryCoilWidths,
    getUnitaryKilowattRatings,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const systemBuilderUnitarySpecificationViewStyles: SystemBuilderUnitarySpecificationViewStyles = {};

const SystemBuilderUnitarySpecificationView: FC<Props> = ({
    extendedStyles,
    getUnitaryBrands,
    getUnitaryTons,
    getUnitaryFurnaceBtus,
    getUnitaryIndoorTypes,
    buildUnitarySystems,
    getUnitaryCoilWidths,
    getUnitaryKilowattRatings,
    step,
    unitaryConfigurations,
}) => {
    const dispatch = useDispatch();
    const [styles] = useState(() => mergeWithCss(systemBuilderUnitarySpecificationViewStyles, extendedStyles));
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!unitaryConfigurations?.brands?.length) {
            getUnitaryBrands({
                onSuccess(result) {
                    const localUnitarySystem = structuredClone(unitaryConfigurations);
                    localUnitarySystem.brands = result?.values;
                    dispatch({
                        type: "xSystemBuilder/UpdateUnitarySystemObject",
                        data: localUnitarySystem,
                    } as any);
                },
            });
        }
    }, []);

    const updateUnitarySystem = (updates: Partial<typeof unitaryConfigurations>) => {
        const updatedSystem = {
            ...structuredClone(unitaryConfigurations),
            ...updates,
        };

        dispatch({
            type: "xSystemBuilder/UpdateUnitarySystemObject",
            data: updatedSystem,
        } as any);
    };

    const selectBrandHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;

        getUnitaryTons({
            parameter: {
                brand: value,
            },
            onSuccess(result) {
                updateUnitarySystem({
                    selectedBrand: value,
                    tons: result?.values,
                    indoorTypes: [],
                    furnaceBtus: [],
                    heatingMethods: ["Hydro Coil"],
                    coilWidths: [],
                    kilowattRatings: [],
                    selectedHeatingMethod: "",
                    selectedTons: "",
                    selectedIndoorType: "",
                    selectedFuranceBtus: "",
                    selectedCoilWidth: "",
                    selectedKilowattRatings: "",
                });
            },
        });
    };

    const selectTonHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        getUnitaryIndoorTypes({
            parameter: {
                brand: unitaryConfigurations?.selectedBrand,
                tons: value,
            },
            onSuccess(result) {
                updateUnitarySystem({
                    indoorTypes: result?.values,
                    furnaceBtus: [],
                    heatingMethods: ["Hydro Coil"],
                    coilWidths: [],
                    kilowattRatings: [],
                    selectedHeatingMethod: "",
                    selectedTons: value,
                    selectedIndoorType: "",
                    selectedFuranceBtus: "",
                    selectedCoilWidth: "",
                    selectedKilowattRatings: "",
                });
            },
        });
    };

    const selectIndoorTypeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        const lowerCasedValue = value.toLowerCase();
        // PLS-1353
        if (lowerCasedValue === "furnace & evaporator coil") {
            return getUnitaryFurnaceBtusOptions(value);
        }

        // PLS-1366
        if (lowerCasedValue === "air handler") {
            return getHeatingMethodsOptions(value);
        }

        if (lowerCasedValue === "evaporator coil") {
            return getCoilWidthsOptions(value);
        }
        updateUnitarySystem({
            furnaceBtus: [],
            selectedHeatingMethod: "",
            selectedIndoorType: value,
            selectedFuranceBtus: "",
            coilWidths: [],
            kilowattRatings: [],
            heatingMethods: ["Hydro Coil"],
            selectedKilowattRatings: "",
            selectedCoilWidth: "",
        });
    };

    const getUnitaryFurnaceBtusOptions = (selectedIndoorType: string) => {
        getUnitaryFurnaceBtus({
            parameter: {
                brand: unitaryConfigurations?.selectedBrand,
                tons: unitaryConfigurations?.selectedTons,
                indoorUnitType: selectedIndoorType,
            },
            onSuccess(result) {
                updateUnitarySystem({
                    furnaceBtus: result?.values,
                    selectedIndoorType,
                    coilWidths: [],
                    kilowattRatings: [],
                    heatingMethods: ["Hydro Coil"],
                    selectedHeatingMethod: "",
                    selectedFuranceBtus: "",
                    selectedCoilWidth: "",
                    selectedKilowattRatings: "",
                });
            },
        });
    };

    const selectFurnaceBtuseHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        updateUnitarySystem({
            selectedFuranceBtus: value,
            selectedHeatingMethod: "",
            coilWidths: [],
            kilowattRatings: [],
            heatingMethods: ["Hydro Coil"],
            selectedCoilWidth: "",
            selectedKilowattRatings: "",
        });
    };

    const getHeatingMethodsOptions = (selectedIndoorType: string) => {
        const { selectedBrand, selectedTons } = unitaryConfigurations;

        getUnitaryKilowattRatings({
            parameter: { brand: selectedBrand, tons: selectedTons },
            onSuccess(result) {
                const options = [
                    ...unitaryConfigurations.heatingMethods,
                    ...(result?.values?.length ? ["Electric Heater Kit"] : []),
                ];

                updateUnitarySystem({
                    selectedIndoorType,
                    heatingMethods: options,
                    kilowattRatings: result?.values,
                    furnaceBtus: [],
                    coilWidths: [],
                    selectedHeatingMethod: "",
                    selectedFuranceBtus: "",
                    selectedKilowattRatings: "",
                    selectedCoilWidth: "",
                });
            },
        });
    };

    const getCoilWidthsOptions = (selectedIndoorType: string) => {
        const { selectedBrand, selectedTons } = unitaryConfigurations;
        getUnitaryCoilWidths({
            parameter: { brand: selectedBrand, tons: selectedTons },
            onSuccess(result) {
                updateUnitarySystem({
                    selectedIndoorType,
                    coilWidths: result?.values,
                    heatingMethods: ["Hydro Coil"],
                    furnaceBtus: [],
                    kilowattRatings: [],
                    selectedHeatingMethod: "",
                    selectedFuranceBtus: "",
                    selectedKilowattRatings: "",
                    selectedCoilWidth: "",
                });
            },
        });
    };

    const selectHeatingMethodHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value.toLowerCase();
        updateUnitarySystem({
            selectedHeatingMethod: value,
            selectedKilowattRatings: "",
            selectedCoilWidth: "",
        });
    };

    const selectCoilWidthHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        updateUnitarySystem({
            selectedCoilWidth: value,
            kilowattRatings: [],
            furnaceBtus: [],
            selectedKilowattRatings: "",
            selectedFuranceBtus: "",
        });
    };

    const selectKilowattRatingsHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        updateUnitarySystem({
            selectedKilowattRatings: value,
            coilWidths: [],
            furnaceBtus: [],
            selectedCoilWidth: "",
            selectedFuranceBtus: "",
        });
    };

    const handleViewSystems = () => {
        // remove localStorage on handleViewSystem click
        localStorage.removeItem(SystemBuilderKey);

        const unitarySystemData = {
            brand: unitaryConfigurations?.selectedBrand,
            tons: Number(unitaryConfigurations?.selectedTons),
            indoorType: unitaryConfigurations?.selectedIndoorType,
            furnaceBtus: Number(unitaryConfigurations?.selectedFuranceBtus),
            heatingMethod: unitaryConfigurations?.selectedHeatingMethod,
            coilWidth: unitaryConfigurations?.selectedCoilWidth,
            electricHeaterKilowattRating: unitaryConfigurations?.selectedKilowattRatings,
        };

        setIsSubmitting(true);
        buildUnitarySystems({
            parameter: {
                ...unitarySystemData,
            },
            onComplete() {
                setIsSubmitting(false);
            },
            onSuccess(result) {
                dispatch({
                    type: "xSystemBuilder/SetSystems",
                    systems: result?.systems,
                    systemData: unitarySystemData,
                } as any);
                dispatch({
                    type: "xSystemBuilder/SetStep",
                    step: step + 1,
                } as any);
            },
        });
    };

    const selectedIndoorType = unitaryConfigurations?.selectedIndoorType;
    const isFurnace = selectedIndoorType?.toLowerCase() === "furnace & evaporator coil";
    const isAirHandler = selectedIndoorType?.toLowerCase() === "air handler";
    const isEvaporator = selectedIndoorType?.toLowerCase() === "evaporator coil";
    const isHydroCoil = unitaryConfigurations.selectedHeatingMethod?.toLowerCase()?.includes("hydro coil");
    const isElectricHeaterKit = unitaryConfigurations.selectedHeatingMethod
        ?.toLowerCase()
        ?.includes("electric heater kit");

    return (
        <LoadingOverlay loading={isSubmitting}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.wrapper}>
                    <Select
                        {...styles.select}
                        value={unitaryConfigurations?.selectedBrand || ""}
                        onChange={selectBrandHandler}
                    >
                        <option value="">{translate("Select Brand")}</option>
                        {unitaryConfigurations?.brands?.map(brand => (
                            <option key={brand} value={brand}>
                                {brand}
                            </option>
                        ))}
                    </Select>

                    {unitaryConfigurations?.tons.length > 0 && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedTons || ""}
                            onChange={selectTonHandler}
                        >
                            <option value="">{translate("Select Tons")}</option>
                            {unitaryConfigurations?.tons?.map(ton => (
                                <option key={ton} value={ton}>
                                    {ton}
                                </option>
                            ))}
                        </Select>
                    )}

                    {unitaryConfigurations?.indoorTypes.length > 0 && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedIndoorType || ""}
                            onChange={selectIndoorTypeHandler}
                        >
                            <option value="">{translate("Select Indoor Type")}</option>
                            {unitaryConfigurations?.indoorTypes?.map(indoorType => (
                                <option key={indoorType} value={indoorType}>
                                    {indoorType}
                                </option>
                            ))}
                        </Select>
                    )}

                    {isFurnace && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedFuranceBtus || ""}
                            onChange={selectFurnaceBtuseHandler}
                        >
                            <option value="">{translate("Select Furnace BTUs")}</option>
                            {unitaryConfigurations?.furnaceBtus?.map(furnaceBtu => (
                                <option key={furnaceBtu} value={furnaceBtu}>
                                    {furnaceBtu}
                                </option>
                            ))}
                        </Select>
                    )}

                    {isAirHandler && !!unitaryConfigurations.heatingMethods.length && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedHeatingMethod || ""}
                            onChange={selectHeatingMethodHandler}
                        >
                            <option value="">{translate("Select Heating Method")}</option>
                            {unitaryConfigurations.heatingMethods?.map(heatingMethod => (
                                <option key={heatingMethod} value={heatingMethod.toLowerCase()}>
                                    {heatingMethod}
                                </option>
                            ))}
                        </Select>
                    )}

                    {isEvaporator && !!unitaryConfigurations.coilWidths.length && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedCoilWidth || ""}
                            onChange={selectCoilWidthHandler}
                        >
                            <option value="">{translate("Select Coil Width")}</option>
                            {unitaryConfigurations?.coilWidths?.map(width => (
                                <option key={width} value={width}>
                                    {width}
                                </option>
                            ))}
                        </Select>
                    )}

                    {isElectricHeaterKit && (
                        <Select
                            {...styles.select}
                            value={unitaryConfigurations?.selectedKilowattRatings || ""}
                            onChange={selectKilowattRatingsHandler}
                        >
                            <option value="">{translate("Select Kilwatt Rating")}</option>
                            {unitaryConfigurations?.kilowattRatings?.map(kwRating => (
                                <option key={kwRating} value={kwRating}>
                                    {kwRating}
                                </option>
                            ))}
                        </Select>
                    )}

                    <StyledWrapper {...styles.divider} />
                    <StyledWrapper {...styles.buttonWrapper}>
                        <Button
                            disabled={
                                !(
                                    (
                                        selectedIndoorType &&
                                        ((isFurnace && unitaryConfigurations?.selectedFuranceBtus) || !isFurnace) &&
                                        (!unitaryConfigurations.selectedHeatingMethod ||
                                            unitaryConfigurations.selectedKilowattRatings ||
                                            isHydroCoil) &&
                                        ((isEvaporator && unitaryConfigurations.selectedCoilWidth) || !isEvaporator)
                                    ) // if selected, then must have at least one of these
                                ) || isSubmitting
                            }
                            onClick={handleViewSystems}
                        >
                            {translate("View Systems")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderUnitarySpecificationView);
